// regelfil: src/AGENTS.md
import { createServerFn, createServerOnlyFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
import { upcomingOpenHouse } from "@/lib/open-house";

/**
 * Publika läsningar av skolans egen profil. En skola räknas som verifierad
 * när en registrerad företrädare har publicerat sin profil — det är den enda
 * signal vi visar för utloggade besökare.
 */
const publicClient = createServerOnlyFn(() => {
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
  return createClient<Database>(process.env["SUPABASE_URL"]!, key, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    global: {
      fetch: async (input, init) => {
        const headers = new Headers(init?.headers);
        if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`) {
          headers.delete("Authorization");
        }
        headers.set("apikey", key);
        const { pageDataFetch } = await import("./page-reads.server");
        return pageDataFetch(input, { ...init, headers });
      },
    },
  });
});

export const getPublishedSchoolProfile = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1) }).parse(input))
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const { schoolCode } = await import("./page-reads.server");
    const code = await schoolCode(supabase, data.slug);
    const unit = code ? { school_unit_code: code } : null;
    if (!unit) return null;

    // Avstängd skola: det skolan själv skrivit visas inte, men sidan med
    // officiella uppgifter finns kvar. Ingenting raderas.
    const { schoolSuspended } = await import("./page-reads.server");
    const suspension = await schoolSuspended(supabase, unit.school_unit_code);
    if (suspension) return null;

    const { data: profile } = await supabase
      .from("school_profiles")
      .select(
        "school_unit_code, description, principal_greeting, pedagogy, student_health, safety_qa, day_schedule, staff, open_house, contact_phone, contact_email, contact_website, published_at",
      )
      .eq("school_unit_code", unit.school_unit_code)
      .eq("moderation_status", "publicerad")
      .maybeSingle();
    if (!profile) return null;
    // Passerade öppet hus-tillfällen lämnar aldrig databasen — en förälder som
    // åker till förra veckans visning är värre än ingen händelse alls.
    return {
      ...profile,
      open_house: upcomingOpenHouse(profile.open_house) as unknown as typeof profile.open_house,
    };
  });

/**
 * Slugar för de skolor som faktiskt är verifierade — dvs. har en publicerad
 * egen profil. Används av kort och listor så att märket aldrig kan påstå mer
 * än vad skolan har gjort.
 */
export const listVerifiedSchoolSlugs = createServerFn({ method: "GET" }).handler(
  async (): Promise<string[]> => {
    const supabase = publicClient();
    const [{ data: profiles }, { data: suspended }] = await Promise.all([
      supabase.from("school_profiles").select("school_unit_code").eq("moderation_status", "publicerad"),
      supabase.from("school_suspensions").select("school_unit_code").is("lifted_at", null),
    ]);
    const avstangda = new Set((suspended ?? []).map((s) => s.school_unit_code));
    const codes = (profiles ?? [])
      .map((p) => p.school_unit_code)
      .filter((c) => Boolean(c) && !avstangda.has(c));
    if (codes.length === 0) return [];
    const { data: units } = await supabase
      .from("education_units")
      .select("slug")
      .in("school_unit_code", codes);
    return (units ?? []).map((u) => u.slug).filter(Boolean);
  },
);
